/**
 * Execution Slice
 *
 * Fetches transmission/execution detail via beaconApi.getExecution().
 * Uses createAsyncThunk with requestId tracking for race condition protection.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { executionInitialState, ExecutionDetailData } from './executionState';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export const fetchExecution = createAsyncThunk<
  ExecutionDetailData, string, { state: RootState; rejectValue: string }
>('execution/fetch', async (executionArn: string, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const result = await beaconApi.getExecution(executionArn, apiBase);
    return result as ExecutionDetailData;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch execution details');
  }
});

const executionSlice = createSlice({
  name: 'execution',
  initialState: executionInitialState,
  reducers: {
    clearExecution: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.currentRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecution.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchExecution.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchExecution.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch execution details';
      });
  },
});

export const { clearExecution } = executionSlice.actions;
export default executionSlice.reducer;
